import type { ReactNode, RefObject } from "react";
import { useState, useCallback, useRef } from "react";
import { ChevronUp } from "lucide-react";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import { useTypeToFocus } from "./useTypeToFocus";
import { pageInfoTarget } from "@/components/page-info/pageInfoTarget";

export type CollapsedChatMode = "idle" | "working" | "reply";

interface CollapsedChatBarProps {
  /** Open the panel on the greeting, conversation, or in-progress reply. */
  onExpand: () => void;
  /** Open the panel carrying the text the user started typing in the pill. */
  onExpandWithDraft: (draft: string) => void;
  /** Open the panel at the unread reply and clear its unread state. */
  onOpenReply: () => void;
  mode: CollapsedChatMode;
  replyPreview?: string | null;
}

/** Stable chat-bubble shell shared by every collapsed launcher state. */
function PillShell({
  onExpand,
  mode,
  children,
}: {
  onExpand: () => void;
  mode: CollapsedChatMode;
  children: ReactNode;
}) {
  const { t } = useLanguage();
  return (
    <div
      role="button"
      tabIndex={0}
      aria-label={t.documents.chatSidebar.expandChat}
      onClick={onExpand}
      {...pageInfoTarget("layout.chat")}
      onKeyDown={(e) => {
        if (e.target !== e.currentTarget) return;
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onExpand();
        }
      }}
      data-testid="collapsed-chat-launcher"
      data-mode={mode}
      className="chat-drag-handle relative flex h-full w-full cursor-pointer items-center gap-3 rounded-[24px] border border-neutral-200 bg-white px-4 shadow-[0px_4px_12px_rgba(0,0,0,0.12),0px_2px_4px_rgba(0,0,0,0.06)] after:absolute after:-bottom-[5px] after:right-6 after:h-3 after:w-3 after:rotate-45 after:border-b after:border-r after:border-neutral-200 after:bg-white"
    >
      {children}
    </div>
  );
}

interface PillInputProps {
  inputRef: RefObject<HTMLInputElement>;
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
}

// The pill never sends directly. The first typed character opens the panel and
// is carried into its full composer.
function PillInput({ inputRef, value, onChange, placeholder }: PillInputProps) {
  return (
    <input
      ref={inputRef}
      type="text"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      onMouseDown={(e) => e.stopPropagation()}
      placeholder={placeholder}
      className="h-8 w-full cursor-pointer border-none bg-transparent px-0 py-1 text-[14px] font-normal leading-6 text-neutral-700 outline-none placeholder:text-neutral-400"
      aria-label="Chat message input"
    />
  );
}

function FranklinMark({ unread }: { unread?: boolean }) {
  const { t } = useLanguage();
  return (
    <span className="relative flex h-7 w-7 shrink-0 items-center justify-center">
      <img
        src="/franklin-logo.svg"
        alt="Franklin"
        draggable={false}
        className="pointer-events-none h-6 w-6 object-contain"
      />
      {unread && (
        <span
          className="absolute -right-1.5 -top-1.5 flex h-[17px] min-w-[17px] items-center justify-center rounded-full bg-primary px-1 text-[10px] font-semibold leading-none text-white ring-2 ring-white"
          aria-label={t.documents.chatSidebar.unreadReply}
        >
          1
        </span>
      )}
    </span>
  );
}

function ThinkingDots() {
  return (
    <span
      className="flex shrink-0 items-center gap-1"
      aria-hidden="true"
      data-testid="thinking-dots"
    >
      {[0, 1, 2].map((index) => (
        <span
          key={index}
          className="h-1.5 w-1.5 animate-bounce rounded-full bg-primary"
          style={{ animationDelay: `${index * 120}ms` }}
        />
      ))}
    </span>
  );
}

/** Unread-reply state: a one-line preview plus the chevron that opens it. */
function ReplyPreviewRow({
  replyPreview,
  onOpenReply,
}: {
  replyPreview?: string | null;
  onOpenReply: () => void;
}) {
  const { t } = useLanguage();
  return (
    <>
      <span className="min-w-0 flex-1 truncate text-[14px] text-neutral-800">{replyPreview}</span>
      <button
        type="button"
        onMouseDown={(event) => event.stopPropagation()}
        onClick={(event) => {
          event.stopPropagation();
          onOpenReply();
        }}
        className="relative z-10 flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-neutral-200 bg-neutral-50 text-neutral-700 transition-colors hover:bg-neutral-100"
        aria-label={t.documents.chatSidebar.openLatestReply}
      >
        <ChevronUp className="h-4 w-4" />
      </button>
    </>
  );
}

export function CollapsedChatBar({
  onExpand,
  onExpandWithDraft,
  onOpenReply,
  mode,
  replyPreview,
}: CollapsedChatBarProps) {
  const [input, setInput] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);
  const { t } = useLanguage();

  useTypeToFocus(inputRef);

  // Typing in the pill opens the panel with the text carried into its input;
  // the example questions stay visible above until the first message is sent.
  const handleChange = useCallback(
    (value: string) => {
      if (value.trim().length === 0) {
        setInput(value);
        return;
      }
      setInput("");
      onExpandWithDraft(value);
    },
    [onExpandWithDraft]
  );

  const handleOpen = mode === "reply" ? onOpenReply : onExpand;

  return (
    <PillShell onExpand={handleOpen} mode={mode}>
      <FranklinMark unread={mode === "reply"} />
      {(mode === "idle" || mode === "working") && (
        // A running turn no longer takes the field away: the first keystroke
        // opens the panel with the draft, and the panel's composer queues it
        // behind the answer. The dots stay as the "he's working" signal.
        <PillInput
          inputRef={inputRef}
          value={input}
          onChange={handleChange}
          placeholder={
            mode === "working"
              ? t.documents.chatSidebar.launcherThinking
              : t.documents.chatSidebar.launcherIdle
          }
        />
      )}
      {mode === "working" && <ThinkingDots />}
      {mode === "reply" && (
        <ReplyPreviewRow replyPreview={replyPreview} onOpenReply={onOpenReply} />
      )}
    </PillShell>
  );
}

CollapsedChatBar.displayName = "CollapsedChatBar";
